import React, { useMemo, useCallback } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { DataGrid } from "@/components/DataGrid/DataGrid";
import { NoRowsOverlay } from "@/features/dmManagement/components/NoRowsOverlay";
import { ADDRESS_DATA_COLUMNS } from "@/features/dmManagement/types/constants";
import type {
  AddressListGridProps,
  ValidationMessage,
} from "@/features/dmManagement/types";
import { formatNumberWithComma } from "@/utils/formatNumber";

const AddressNoRowsOverlay = (): JSX.Element => (
  <NoRowsOverlay message="宛先データがありません" />
);

const dataGridSx = {
  height: 400,
  "& .address-error-row": {
    backgroundColor: "#ffebee",
  },
  "& .address-warning-row": {
    backgroundColor: "#fff8e1",
  },
} as const;

const dataGridComponents = { NoRowsOverlay: AddressNoRowsOverlay } as const;

interface GridRow {
  id: number;
  number: number;
  postalCode: string;
  companyName: string;
  branchDepartment: string;
  titleDepartment: string;
  name: string;
  honorific: string;
  address1: string;
  address2: string;
  remarks: string;
  _errors: ValidationMessage[];
  _warnings: ValidationMessage[];
}

/**
 * 宛先リストグリッド
 * 宛先データをDataGridで一覧表示し、エラー・警告がある行をハイライト表示する
 */
export const AddressListGrid: React.FC<AddressListGridProps> = ({
  rows,
  totalRows,
  hasError,
  hasWarning,
}) => {
  const gridRows: GridRow[] = useMemo(
    () =>
      rows.map((row) => ({
        id: row.rowNumber,
        ...row.data,
        _errors: row.errors,
        _warnings: row.warnings,
      })),
    [rows]
  );

  /** 番号カラムのカスタムレンダラー。エラー/警告がある行にアイコン+ツールチップを表示する */
  const renderNumberCell = useCallback(
    (params: GridRenderCellParams): React.ReactNode => {
      const row = params.row as GridRow;
      const hasErrors = row._errors.length > 0;
      const hasWarnings = row._warnings.length > 0;

      if (!hasErrors && !hasWarnings) {
        return <>{formatNumberWithComma(row.number)}</>;
      }

      const messages = [
        ...row._errors.map((e) => e.message),
        ...row._warnings.map((w) => w.message),
      ];
      const iconColor: "error" | "warning" = hasErrors ? "error" : "warning";

      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {formatNumberWithComma(row.number)}
          <Tooltip
            title={messages.map((msg, i) => (
              <div key={i}>{msg}</div>
            ))}
          >
            <PriorityHighIcon color={iconColor} sx={{ fontSize: 16 }} />
          </Tooltip>
        </Box>
      );
    },
    []
  );

  const columns: GridColDef[] = useMemo(() => {
    const dataColumns: GridColDef[] = Object.entries(ADDRESS_DATA_COLUMNS).map(
      ([field, headerName]) => ({
        field,
        headerName,
        width: field === "address1" || field === "address2" ? 200 : 120,
        sortable: false,
        ...(field === "number" && { renderCell: renderNumberCell }),
      })
    );

    const statusColumn: GridColDef = {
      field: "_status",
      headerName: "状態",
      width: 250,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as GridRow;
        if (row._errors.length > 0) {
          return (
            <Typography variant="body2" color="error" noWrap>
              {row._errors.map((e) => e.message).join(", ")}
            </Typography>
          );
        }
        if (row._warnings.length > 0) {
          return (
            <Typography variant="body2" color="warning.main" noWrap>
              {row._warnings.map((w) => w.message).join(", ")}
            </Typography>
          );
        }
        return null;
      },
    };

    return [...dataColumns, statusColumn];
  }, [renderNumberCell]);

  const getRowClassName = useCallback((params: { row: GridRow }): string => {
    if (params.row._errors.length > 0) return "address-error-row";
    if (params.row._warnings.length > 0) return "address-warning-row";
    return "";
  }, []);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        宛先リストプレビュー（{formatNumberWithComma(totalRows)}件）
        {hasError && (
          <Typography
            component="span"
            color="error"
            variant="body2"
            sx={{ ml: 1 }}
          >
            エラーあり
          </Typography>
        )}
        {hasWarning && !hasError && (
          <Typography
            component="span"
            color="warning.main"
            variant="body2"
            sx={{ ml: 1 }}
          >
            警告あり
          </Typography>
        )}
      </Typography>
      <DataGrid
        rows={gridRows}
        columns={columns}
        pageSize={50}
        rowsPerPageOptions={[50]}
        getRowClassName={getRowClassName}
        sx={dataGridSx}
        components={dataGridComponents}
      />
    </Box>
  );
};
